import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import EditButton from "../../../../../components/EditButton";
import SaveButton from "../../../../../components/SaveButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import TextInput from "../../../../../components/TextInput";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import TexasGroupingForm from "../form/TexasGroupingForm";
import { ITexasGroupingDetails } from "../types";
import {
  addStateTexasGrouping,
  editRowIdTexasGrouping,
  editStateTexasGrouping,
  selectedRowsStateTexasGrouping,
  rowForEditTexasGrouping,
  savingTexasGrouping,
} from "./../atoms/atoms";
import { deleteTexasGrouping } from "../../../../../actions/Utilities/TexasGrouping";
import useGetTexasGroupings from "./../hooks/useGetTexasGrouping";
import useEditTexasGrouping from "./../hooks/useUpdateTexasGrouping";

const TexasGroupingTable = () => {
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateTexasGrouping
  );
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateTexasGrouping
  );
  const [texasGrouping, setTexasGrouping] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [texasGroupingFilteredData, setTexasGroupingFilteredData] = useState(
    []
  );
  const [disabledButton, setDisabledButton] = useState(false);
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_texasgrouping_add;
  const editPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_texasgrouping_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_officeconfiguration_texasgrouping_delete;
  const [displayNote] = useState(false);
  const queryClient = useQueryClient();
  const [checkStatus, setCheckStatus] = useState(false);
  const [texasDataIsChange, setTexasDataIsChange] = useState(false);
  const [newData, setNewData] = useState<ITexasGroupingDetails>();
  const [editRowId, setEditRowId] = useRecoilState(editRowIdTexasGrouping);
  const [edit, setEdit] = useState(false);
  const [hasTexas] = useState(true);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [rowsCount, setRowsCount] = useState(0);
  const [hideColumn, sethideColumn] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [, setRowToEdit] = useRecoilState(rowForEditTexasGrouping);
  const [, setTexasExistState] = useState(false);
  const [, setSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(savingTexasGrouping);
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateTexasGrouping
  );
  const {
    data: texasGroupingData,
    isLoading: isFetching,
    isSuccess: texasGroupingSuccess,
  } = useGetTexasGroupings();
  useEffect(() => {
    if (texasGroupingSuccess && texasGroupingData) {
      if (texasGroupingData?.status === 200) {
        setTexasGrouping(texasGroupingData?.data.map((data) => ({ ...data })));
      } else {
        toast.fire(
          "Server Error",
          "Cannot retrieve Texas Grouping Data",
          "error"
        );
      }
      setTexasGrouping([]);
    }
  }, [texasGroupingData, texasGroupingSuccess]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state == null) {
    //Do nothing
  } else {
    recordIdFromRecent = location.state;
  }

  const alignCenter = "center" as
    | "center"
    | "inherit"
    | "justify"
    | "left"
    | "right"
    | undefined;

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setTexasGroupingFilteredData(result);
          setTexasGroupingId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  const matColumns = [
    {
      title: "Texas Grouping Name",
      field: "texasGroupingName",
      customSort: (a, b) =>
        a.texasGroupingName && b.texasGroupingName
          ? a.texasGroupingName.trim().localeCompare(b.texasGroupingName.trim())
          : a.texasGroupingName
          ? 0
          : -1,
      render: (rowData) => {
        return rowData.texasGroupingId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer">
            <TextInput
              placeholder="Texas Grouping Name"
              defaultValue={rowData.texasGroupingName}
              onChange={(inputdata) => {
                handleEditFieldsChange(inputdata.target.value);
              }}
              value={undefined}
            />
          </div>
        ) : (
          <span>{rowData.texasGroupingName}</span>
        );
      },
    },
    {
      title: "Created By",
      field: "createdBy",
      hidden: hideColumn,
      render: (rowData) => {
        return rowData.texasGroupingId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>{rowData.createdBy}</>
        );
      },
    },
    {
      title: "Created Date",
      field: "createdDate",
      hidden: hideColumn,
      render: (rowData) => {
        return rowData.texasGroupingId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>
            {rowData?.createdDate
              ? new Date(rowData.createdDate).toLocaleDateString()
              : ""}
          </>
        );
      },
    },
    {
      title: "Modified By",
      field: "updatedBy",
      hidden: hideColumn,
      render: (rowData) => {
        return rowData.texasGroupingId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>{rowData.updatedBy}</>
        );
      },
    },
    {
      title: "Modified Date",
      field: "updatedDate",
      hidden: hideColumn,
      render: (rowData) => {
        return rowData.texasGroupingId === editRowId ? (
          <div className="flex items-center space-x-1 cursor-pointer"></div>
        ) : (
          <>
            {rowData?.updatedDate
              ? new Date(rowData.updatedDate).toLocaleDateString()
              : ""}
          </>
        );
      },
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData.texasGroupingId === editRowId ? (
          <div className="flex space-x-1 cursor-pointer">
            <input
              type="checkbox"
              className="flex items-center space-x-1 cursor-pointer mt-0.5"
              defaultChecked={rowData.isActive}
              onClick={(e) => {
                handleCheckBox(e);
              }}
            />
            <label className="items-center cursor-pointer text-green-600 text-xs">
              {checkStatus ? "Uncheck to Deactivate" : "Check to Activate"}
            </label>
          </div>
        ) : (
          <div>
            <div className="font-sans text-left mr-4 text-xs">
              {rowData.isActive ? "Active" : "Inactive"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action/s",
      align: alignCenter,
      render: (rowData) => {
        return !userPermissions.includes(editPermission) ? (
          ""
        ) : rowData.texasGroupingId === editRowId ? (
          <div className="flex items-center space-x-2 w-full mr-2">
            <SaveButton
              className={`flex items-center shadow-md text-xs font-semibold text-white  gap-x-1 rounded-full  bg-senary hover:bg-senary px-3 py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-senary focus:ring-opacity-70
              ${
                !hasTexas || !texasDataIsChange ? "opacity-50" : "opactiy-100"
              }`}
              disabled={!hasTexas || !texasDataIsChange}
              onClick={() => {
                setOpenModalEdit(true);
              }}
            />
            <CancelButton
              onClick={(event) => {
                event.preventDefault();
                handleCancel();
              }}
            />
          </div>
        ) : (
          <div className="flex items-center place-content-center mr-4">
            <EditButton
              disabled={disabledButton || selectedRows.length > 0}
              className={
                disabledButton || selectedRows.length > 0
                  ? "opacity-50 rounded-full items-center bg-quaternary  text-white hover:bg-hquaternary "
                  : "opacity-100 rounded-full items-center bg-quaternary  text-white hover:bg-hquaternary"
              }
              onClick={() => {
                setDisabledButton(true);
                setActionEditState(true);
                handleEdit(rowData);
                setNewData(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  //Deactivate Toast
  const deactTexasGrouping = useMutation(
    (ids: number[] | undefined) => deleteTexasGrouping(ids),
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        toast.fire(
          "Success",
          "Texas Grouping were deactivated successfully",
          "success"
        );
      },
      onError: () => {
        toast.fire(
          "Bulk deactivation of Texas Grouping failed",
          "Server Error: Cannot deactivate Texas Grouping",
          "error"
        );
      },
    }
  );

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Texas Groupings" : "Texas Grouping"
    }?`}</div>
  );

  // Edit Result
  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const [texasGroupingId, setTexasGroupingId] = useState<any>("");
  useEffect(() => {
    if (filterText) {
      setTexasGrouping(
        texasGroupingData?.data.filter((x) =>
          x.texasGroupingName
            ?.toLowerCase()
            .includes(filterText?.trim().toLowerCase())
        )
      );
    } else if (texasGroupingId?.includes(".")) {
      let texasGroupingIds = texasGroupingId.split(".");
      const texasGroupingIdssArr = texasGroupingIds.map((id) => Number(id));
      const filteredData = texasGroupingData?.data?.filter((x) =>
        texasGroupingIdssArr.includes(x.texasGroupingId)
      );

      setTexasGroupingFilteredData(filteredData);
    } else {
      if (
        texasGroupingData?.data.filter(
          (x) => x.texasGroupingId === Number(texasGroupingId)
        )
      ) {
        const filteredData = texasGroupingData?.data.filter(
          (x) => x.texasGroupingId === Number(texasGroupingId)
        );
        setTexasGroupingFilteredData(filteredData);
      }
      setTexasGrouping(texasGroupingData?.data);
    }
  }, [filterText, texasGroupingData?.data]);

  const handleDelete = () => {
    const ids = selectedRows.map((row) => row.texasGroupingId);
    setSelectedRows([]);
    setOpenModalDelete(false);
    deactTexasGrouping.mutate(ids);
  };

  // Edit Texas  Grouping
  const {
    mutate: editTexasGrouping,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useEditTexasGrouping();
  useEffect(() => {
    setTexasExistState(false);
    setTexasDataIsChange(false);
    setActionAddState(false);
    setActionEditState(false);
    setSelectedRows([]);
    setEditRowId([]);
  }, []);

  const [editRowData, setEditRowData] = useState<any>();
  const handleEdit = (rowDetails) => {
    setRowToEdit({ ...rowDetails, isActive: rowDetails.isActive });
    setActionEditState(true);
    sethideColumn(true);
    setTexasDataIsChange(false);
    setCheckStatus(rowDetails.isActive);
    setEditRowId(rowDetails.texasGroupingId);
    setEditRowData({ ...rowDetails });
  };

  const handleEditFieldsChange = (inputvalue) => {
    setTexasExistState(false);
    setTexasDataIsChange(false);
    inputvalue = inputvalue.trim();

    newData!.texasGroupingName = inputvalue;
    setTexasDataIsChange(true);
    if (newData?.texasGroupingName.length === 0) {
      setTexasDataIsChange(false);
    }
  };

  const handleCheckBox = (e) => {
    const checked = e.target.checked;
    setTexasDataIsChange(true);
    checked ? setCheckStatus(true) : setCheckStatus(false);
    newData!.isActive = checked;
    if (newData?.texasGroupingName.length === 0) {
      setTexasDataIsChange(false);
    }
  };

  //Save changes
  const [data, setData] = useState<any>();
  const handleSave = (newData) => {
    editTexasGrouping(newData);
    setEditRowId([]);
    setSelectedRows([]);
    setTexasExistState(false);
    setTexasDataIsChange(false);
    setOpenModalEdit(false);
    setActionEditState(false);
    setDisabledButton(false);
    setEdit(false);
    sethideColumn(false);
    setData({ ...newData });
  };

  const handleCancel = () => {
    setActionEditState(false);
    setRowToEdit(undefined);
    setEditRowId([]);
    setTexasDataIsChange(false);
    setTexasExistState(false);
    setDisabledButton(false);
    setEdit(false);
    sethideColumn(false);
    queryClient.invalidateQueries();
  };

  if (editSuccess && editResult) {
    if (
      editRowData?.texasGroupingName === data?.texasGroupingName &&
      editRowData?.isActive === data?.isActive
    ) {
      setIsSaving(false);
      toast.fire(
        "Failed",
        "Failed to Update Texas Grouping! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries();
      setActionEditState(false);
    } else if (
      (editResult && editResult.status === 400) ||
      editResult.data === "Duplicate"
    ) {
      toast.fire("Failed", "Texas Grouping Already Exists.", "error");
      queryClient.invalidateQueries();
      setIsSaving(!isSaving);
      setSaveModal(false);
      // setActionEditState(true);
    } else if (
      (editResult && editResult.status === 201) ||
      editResult.status === 200
    ) {
      toast.fire("Success", "Texas Grouping Updated Successfully.", "success");
      queryClient.invalidateQueries();
      handleCancel();
      setRowToEdit(undefined);
      setActionEditState(false);
    } else if (editResult.status === 500) {
      if (
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Texas Grouping updated successfully, but " +
            editResult.data.message,
          "warning"
        );
        setIsSaving(false);
        handleCancel();
        setRowToEdit(undefined);
      } else {
        setIsSaving(false);
        setRowToEdit(undefined);
        toast.fire(
          "Failed",
          "Failed to Update Texas Grouping! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries();
      setActionEditState(false);
    }
    resetEditData();
  }
  if (editError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Update Texas Grouping! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries();
    setActionEditState(false);
  }

  return (
    <>
      <div className="animate-fade-in-down">
        <div className="">
          {!actionAddState && (
            <div className="">
              <div className="bg-white p-4 space-y-2">
                <div className="bg-white w-ful">
                  <div className="flex flex-row justify-between items-center">
                    <div className="">
                      <TableFilters
                        moduleDetails={"Texas Grouping Name"}
                        textInputName={"texasGroupingName"}
                        filterText={setFilterText}
                        disabledButton={
                          actionEditState ||
                          (selectedRows !== undefined &&
                            selectedRows.length > 0)
                        }
                      />
                    </div>
                    <div className="">
                      {userPermissions.includes(addPermission) ? (
                        <AddButton
                          buttonText="New Texas Grouping"
                          onClick={() => setActionAddState(true)}
                          disabled={
                            actionEditState ||
                            (selectedRows !== undefined &&
                              selectedRows.length > 0)
                          }
                        />
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                  {userPermissions.includes(deletePermission) ? (
                    <div className="text-secondary italic text-xs3 my-2">
                      <p className="font-bold italic inline-block mr-1">
                        Note:
                      </p>
                      <span>Tick the box to deactivate some data.</span>
                    </div>
                  ) : (
                    <div className="mb-4"></div>
                  )}

                  <div className="rounded-md box-shadow border-t overflow-x-auto">
                    <MaterialTableTS
                      data={
                        texasGroupingFilteredData.length > 0
                          ? texasGroupingFilteredData
                          : texasGrouping
                      }
                      showTitle={true}
                      columns={matColumns}
                      isLoading={isFetching}
                      onSelectionChange={handleSelectedRowsChange}
                      selectedRows={selectedRows}
                      selection={
                        !actionEditState &&
                        !actionAddState &&
                        userPermissions.includes(deletePermission)
                      }
                      setConfirmModalOpen={setOpenModalDelete}
                    />
                  </div>
                </div>
              </div>
              <ConfirmBoxModal
                ModalTitle="Update Texas Grouping"
                qConfirmation=" Are you sure you want to update this?"
                isOpen={openModalEdit}
                onClose={() => {
                  setOpenModalEdit(false);
                  setIsSaving(false);
                }}
                onClickFunction={() => {
                  handleSave(newData);
                  setIsSaving(false);
                }}
                cancelButtonRef={undefined}
              />
              <ConfirmBoxDelete
                ModalTitle="Deactivate Texas Grouping"
                qConfirmation={deleteMessage}
                isOpen={openModalDelete}
                onClose={() => {
                  setOpenModalDelete(false);
                  setSelectedRows([]);
                }}
                onClickFunction={handleDelete}
                cancelButtonRef={undefined}
              />
            </div>
          )}
          {actionAddState && (
            <div className="pb-4">
              <TexasGroupingForm
                isAdd={actionAddState}
                setIsAdd={setActionAddState}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TexasGroupingTable;
